import { PureComponent, ReactNode } from 'react';

import { RouteComponentProps } from 'src/router/types';
import { withRouter } from 'src/router/utils';

import { RangeSelection } from '../types';
import {
  buildLineUrlFragment,
  updateActiveRanges,
  extendRangeUrlFragment,
  parseLineNums,
} from '../utils/parse-hash';

export type RenderProps = {
  selectedRanges: RangeSelection[];
  handleLineNumClick: (e: MouseEvent, lineNum: number) => void;
};

type Props = RouteComponentProps & {
  render: (props: RenderProps) => ReactNode;
  targetUrl?: string;
};

type State = {
  selectedRanges: RangeSelection[];
};

class SelectedLineProvider extends PureComponent<Props, State> {
  state = {
    selectedRanges: [],
  };

  componentDidMount() {
    /**
     * Ideally we would listen for changes to `this.props.location.hash` instead of the native
     * "hashchange" event to have a better hook into the router and to mocking scenarios in
     * testing easier. However, this value doesn't change in response to any of the following:
     *
     * - window.location.hash = 'newFragment'
     * - this.props.history.push(`${currentUrl}#newFragment`)
     * - Updating the URL manually in the browser to change the fragment
     *
     */
    window.addEventListener('hashchange', this.updateSelectionFromUrlFragment);
    this.updateSelectionFromUrlFragment();
  }

  componentWillUnmount() {
    window.removeEventListener(
      'hashchange',
      this.updateSelectionFromUrlFragment
    );
  }

  buildUrlFragmentFromLineNumClick = (e: MouseEvent, lineNum: number) => {
    const { selectedRanges } = this.state;

    // if this is the first click, always update with a single line
    if (selectedRanges.length === 0) {
      return buildLineUrlFragment(lineNum);
    }

    // when holding down meta or ctrl, preserve the existing selection and add or remove the new line
    if (e.metaKey || e.ctrlKey) {
      return updateActiveRanges(selectedRanges, lineNum);
    }

    // when holding down shift, update the most recently selected line/range to fill the lines between
    if (e.shiftKey) {
      return extendRangeUrlFragment(selectedRanges, lineNum);
    }

    return buildLineUrlFragment(lineNum);
  };

  handleLineNumClick = (e: MouseEvent, lineNum: number) => {
    e.preventDefault();

    const { history, location, targetUrl } = this.props;

    const newFragment = this.buildUrlFragmentFromLineNumClick(e, lineNum);

    if (newFragment) {
      if (!targetUrl || targetUrl === location.pathname) {
        window.location.hash = newFragment;
      } else {
        history.push(`${targetUrl}#${newFragment}`);
        // Changes to the URL fragment caused by `history.push` do not fire a "hashchange" event,
        // so we have to manually trigger the state update
        this.updateSelectionFromUrlFragment();
      }
    }
  };

  updateSelectionFromUrlFragment = () => {
    const { hash: urlFragment } = window.location;
    this.setState({
      selectedRanges: urlFragment ? parseLineNums(urlFragment) : [],
    });
  };

  render() {
    const { selectedRanges } = this.state;
    return this.props.render({
      handleLineNumClick: this.handleLineNumClick,
      selectedRanges,
    });
  }
}

export default withRouter(SelectedLineProvider);
